import React, { useMemo } from 'react';

import { Col, Row } from 'antd';

import { AdditionalRequiredActiveProduct } from 'common/components/product-add/make-product-active/AdditionalRequiredActiveProduct';

import { RequiredPropertyInput } from 'common/components/product-add/make-product-active/RequiredPropertyInput';

import { StyledModal } from 'common/components';

import {
  RequiredModuleName,
  RequiredPropertiesForm,
  RequiredPropertiesSection,
  RequiredPropertyItem,
  RequiredPropertyName,
  RequiredPropertyStatus,
} from 'common/components/product-add/make-product-active/RequiredPropertyElement';

import {
  useRequiredActiveProductContext,
  useUpdateUnmatchedRequirement,
} from 'common/components/product-add/make-product-active/hooks';
import { useAllMinimumRequirementProductFields } from 'hooks/entityHooks';

import { mappingGridProperties as groupPropertiesByModuleName } from 'pages/home/ribbon/components/sections/customize-grid/utils';

import { filterInvalidValuesFormValues } from 'common/components/product-add/make-product-active/utils';
import { getPropertiesParams } from 'utils/minimumRequirementUtils';

import { additionalRequirementFields } from 'common/components/product-add/make-product-active/constants';

import {
  modalBodyProductActiveHeight,
  modalMakeProductActiveWidth,
} from '../ixone-credit-card';

export const ProductsActiveValidating = ({ minimumRequirements }) => {
  const { flatRequiredProductFields } = useAllMinimumRequirementProductFields();
  const { requiredForm } = useRequiredActiveProductContext();

  const { id: entityId, errorFields } = minimumRequirements;

  const additionalFields = Object.keys(additionalRequirementFields);

  const filteredEntityRequirement = useMemo(() => {
    let result = [];

    if (
      errorFields?.length > 0 &&
      Object.keys(flatRequiredProductFields)?.length > 0
    ) {
      errorFields.forEach((requiredItem) => {
        const foundProperty = flatRequiredProductFields[`${requiredItem}`];
        foundProperty && result.push(foundProperty);
      });
    }

    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorFields, Object.keys(flatRequiredProductFields)?.length]);

  const groupedRequiredProperties = useMemo(() => {
    if (filteredEntityRequirement.length === 0) return [];

    return groupPropertiesByModuleName(filteredEntityRequirement);
  }, [filteredEntityRequirement]);

  const additionalRequiredErrorFields = errorFields.filter((field) =>
    additionalFields.includes(field)
  );

  return (
    <Row style={{ width: '100%' }} gutter={[0, 6]} className='required-section'>
      {groupedRequiredProperties?.length > 0 && (
        <RequiredPropertiesSection header='Required Properties'>
          <RequiredPropertiesForm form={requiredForm} style={{ width: '100%' }}>
            {groupedRequiredProperties.map((groupItem) => {
              return (
                <Col span={24}>
                  <RequiredModuleName
                    moduleName={groupItem.moduleDisplayName}
                    key={groupItem.moduleDisplayName}
                  />
                  {groupItem.moduleProperties.map((property) => {
                    const isError = errorFields.includes(
                      property.fieldFullPath
                    );

                    return (
                      <RequiredPropertyItem key={property.fullPathDisplayName}>
                        <RequiredPropertyName
                          key={property.fullPathDisplayName}
                          propertyName={property.fullPathDisplayName}
                          isError={isError}
                        />
                        <RequiredPropertyStatus
                          status={isError ? 'error' : 'success'}
                        />

                        {isError && (
                          <RequiredPropertyInput
                            property={property}
                            status='error'
                          />
                        )}
                      </RequiredPropertyItem>
                    );
                  })}
                </Col>
              );
            })}
          </RequiredPropertiesForm>
        </RequiredPropertiesSection>
      )}

      {additionalRequiredErrorFields?.length > 0 && (
        <RequiredPropertiesSection header='Additional Properties'>
          <AdditionalRequiredActiveProduct
            requiredErrorFields={additionalRequiredErrorFields}
            entityId={entityId}
          />
        </RequiredPropertiesSection>
      )}
    </Row>
  );
};

export const ProductsActiveValidatingModal = ({
  entityId,
  children,
  onRevalidateActiveProduct = () => {},
  onCancel,
  ...otherProps
}) => {
  const { requiredForm } = useRequiredActiveProductContext();

  const unmatchedRequirementMutation = useUpdateUnmatchedRequirement();

  const handleUpdateUnmatchedRequirement = () => {
    const formValues = filterInvalidValuesFormValues(
      requiredForm.getFieldsValue()
    );

    const requestParams = {
      entityId,
      entityName: 'PIM',
      properties: getPropertiesParams(formValues),
    };

    unmatchedRequirementMutation.mutate(requestParams, {
      onSuccess: () => {
        onRevalidateActiveProduct();
        onCancel();
      },
    });
  };

  const modalProps = {
    title: 'Update Required Values',
    width: modalMakeProductActiveWidth,
    bodyStyle: {
      ...modalBodyProductActiveHeight,
    },
    wrapClassName: 'make-product-active__validating-modal',
    closable: true,
    maskClosable: false,
    onOk: handleUpdateUnmatchedRequirement,
    onCancel,
    okButtonProps: {
      loading: unmatchedRequirementMutation.isLoading,
    },
    ...otherProps,
  };

  return <StyledModal {...modalProps}>{children}</StyledModal>;
};
